/**
 * useProfilePhotoCapture Hook
 *
 * Hook per gestire la cattura e upload di foto profilo cliente.
 * Supporta sia cattura da camera che upload da galleria.
 *
 * Features:
 * - Camera capture con MediaDevices API
 * - File picker per galleria
 * - Compressione WebP 85% quality
 * - Upload automatico a /api/accettazione-mobile/upload-customer-photo
 * - Gestione errori e loading states
 */

import { useState, useRef } from 'react';

export interface ProfilePhotoState {
  photoUrl: string | null;         // URL locale blob o URL server
  photoFile: File | null;           // File da caricare
  isCapturing: boolean;             // Camera attiva
  isUploading: boolean;             // Upload in corso
  uploadedUrl: string | null;       // URL finale dal server
  error: string | null;             // Messaggio errore
}

export interface ProfilePhotoCaptureOptions {
  /** Camera da usare: 'user' (frontale/selfie) o 'environment' (posteriore). Default: 'environment' */
  facingMode?: 'user' | 'environment';
}

export function useProfilePhotoCapture(options: ProfilePhotoCaptureOptions = {}) {
  const { facingMode = 'environment' } = options;
  const [state, setState] = useState<ProfilePhotoState>({
    photoUrl: null,
    photoFile: null,
    isCapturing: false,
    isUploading: false,
    uploadedUrl: null,
    error: null
  });

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  /**
   * Avvia la camera per cattura foto
   */
  const startCapture = async () => {
    try {
      setState(prev => ({ ...prev, isCapturing: true, error: null }));

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode, // Configurabile: 'environment' (posteriore) o 'user' (frontale)
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });

      streamRef.current = stream;

      // Se esiste videoRef, assegna lo stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      console.log('[useProfilePhotoCapture] Camera avviata');
    } catch (error) {
      console.error('[useProfilePhotoCapture] Errore avvio camera:', error);
      setState(prev => ({
        ...prev,
        isCapturing: false,
        error: 'Impossibile accedere alla camera. Verifica i permessi.'
      }));
    }
  };

  /**
   * Scatta la foto dalla camera
   */
  const capturePhoto = async (): Promise<void> => {
    if (!videoRef.current || !streamRef.current) {
      setState(prev => ({ ...prev, error: 'Camera non attiva' }));
      return;
    }

    try {
      // Crea canvas per catturare frame video
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Impossibile creare context canvas');
      }

      // Disegna frame corrente
      ctx.drawImage(video, 0, 0);

      // Converti a Blob (WebP 85% quality)
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/webp', 0.85);
      });

      if (!blob) {
        throw new Error('Errore conversione immagine');
      }

      // Crea File object
      const file = new File([blob], `profile_${Date.now()}.webp`, {
        type: 'image/webp'
      });

      // Crea URL locale per preview
      const photoUrl = URL.createObjectURL(blob);

      setState(prev => ({
        ...prev,
        photoUrl,
        photoFile: file,
        isCapturing: false,
        error: null
      }));

      // Ferma la camera
      stopCapture();

      console.log('[useProfilePhotoCapture] Foto catturata:', file.size, 'bytes');
    } catch (error) {
      console.error('[useProfilePhotoCapture] Errore cattura foto:', error);
      setState(prev => ({
        ...prev,
        error: 'Errore durante lo scatto della foto'
      }));
    }
  };

  /**
   * Ferma la camera
   */
  const stopCapture = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setState(prev => ({ ...prev, isCapturing: false }));
    console.log('[useProfilePhotoCapture] Camera fermata');
  };

  /**
   * Apri file picker per caricare da galleria
   */
  const uploadFromGallery = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];

      if (!file) return;

      try {
        // Verifica dimensione file (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          setState(prev => ({
            ...prev,
            error: 'File troppo grande. Massimo 5MB.'
          }));
          return;
        }

        // Comprimi immagine se necessario
        const compressedFile = await compressImage(file);

        // Crea URL preview
        const photoUrl = URL.createObjectURL(compressedFile);

        setState(prev => ({
          ...prev,
          photoUrl,
          photoFile: compressedFile,
          error: null
        }));

        console.log('[useProfilePhotoCapture] Foto caricata da galleria:', compressedFile.size, 'bytes');
      } catch (error) {
        console.error('[useProfilePhotoCapture] Errore caricamento galleria:', error);
        setState(prev => ({
          ...prev,
          error: 'Errore durante il caricamento della foto'
        }));
      }
    };

    input.click();
  };

  /**
   * Comprimi immagine a 800px max e WebP 85%
   */
  const compressImage = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };

      reader.onerror = reject;

      img.onload = () => {
        // Calcola dimensioni target (max 800px mantenendo aspect ratio)
        const maxSize = 800;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        // Crea canvas e ridimensiona
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Impossibile creare context canvas'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Converti a WebP
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Errore compressione immagine'));
              return;
            }

            const compressedFile = new File([blob], `profile_${Date.now()}.webp`, {
              type: 'image/webp'
            });

            resolve(compressedFile);
          },
          'image/webp',
          0.85
        );
      };

      img.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  /**
   * Rimuovi foto corrente
   */
  const removePhoto = () => {
    // Revoca URL blob per liberare memoria
    if (state.photoUrl && state.photoUrl.startsWith('blob:')) {
      URL.revokeObjectURL(state.photoUrl);
    }

    setState({
      photoUrl: null,
      photoFile: null,
      isCapturing: false,
      isUploading: false,
      uploadedUrl: null,
      error: null
    });

    console.log('[useProfilePhotoCapture] Foto rimossa');
  };

  /**
   * Upload foto al server
   *
   * @param customerName Nome del cliente (usato per naming file)
   * @param customerId ID del cliente (opzionale, se cliente esistente)
   * @returns URL della foto caricata sul server
   */
  const uploadPhoto = async (
    customerName: string,
    customerId?: string
  ): Promise<string> => {
    if (!state.photoFile) {
      throw new Error('Nessuna foto da caricare');
    }

    setState(prev => ({ ...prev, isUploading: true, error: null }));

    try {
      const formData = new FormData();
      formData.append('photo', state.photoFile);
      formData.append('customerName', customerName);

      if (customerId) {
        formData.append('customerId', customerId);
      }

      console.log('[useProfilePhotoCapture] 📤 Upload foto profilo...');

      const response = await fetch('/api/accettazione-mobile/upload-customer-photo', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Errore upload foto');
      }

      const data = await response.json();
      const uploadedUrl = data.photo?.url || data.photo_url; // Backend ritorna photo.url

      setState(prev => ({
        ...prev,
        isUploading: false,
        uploadedUrl,
        error: null
      }));

      console.log('[useProfilePhotoCapture] ✅ Foto caricata:', uploadedUrl);

      return uploadedUrl;
    } catch (error) {
      console.error('[useProfilePhotoCapture] ❌ Errore upload:', error);

      const errorMessage = error instanceof Error ? error.message : 'Errore sconosciuto';

      setState(prev => ({
        ...prev,
        isUploading: false,
        error: errorMessage
      }));

      throw error;
    }
  };

  /**
   * Reset completo dello stato
   */
  const reset = () => {
    stopCapture();
    removePhoto();
  };

  return {
    // State
    photoUrl: state.photoUrl,
    photoFile: state.photoFile,
    isCapturing: state.isCapturing,
    isUploading: state.isUploading,
    uploadedUrl: state.uploadedUrl,
    error: state.error,

    // Actions
    startCapture,
    capturePhoto,
    stopCapture,
    uploadFromGallery,
    removePhoto,
    uploadPhoto,
    reset,

    // Refs (per video element)
    videoRef
  };
}
